import { useState } from 'react';
import { Button } from '@rudi/ui/button';
import {
  usePlatformVoiceWritingOptions,
  usePlatformVoiceWritingPolicy,
  useRemovePlatformVoiceWritingPolicy,
  useSavePlatformVoiceWritingPolicy,
} from '../api/hooks';
import { VoiceWritingPolicyForm } from './TenantVoiceWritingSection';

// Rudi owns dictation routing. This page edits Rudi's platform default, which
// every tenant without its own voice and writing policy uses on its next capture.
export function DictationSettings({ onSetKey }: { onSetKey: (keyName: string) => void }) {
  const policy = usePlatformVoiceWritingPolicy();
  const options = usePlatformVoiceWritingOptions();
  const save = useSavePlatformVoiceWritingPolicy();
  const remove = useRemovePlatformVoiceWritingPolicy();
  const [removeError, setRemoveError] = useState<string | null>(null);
  return (
    <section
      className="mt-8 min-w-0 space-y-4 rounded-3xl border p-5"
      data-testid="dictation-settings"
    >
      <div>
        <h2 className="text-lg font-semibold">Dictation</h2>
        <p className="text-sm text-muted-foreground">
          The platform default decides how Rudi transcribes dictation for every tenant without its
          own voice and writing policy. Choose the primary provider and backups, then set each
          provider's key. A backup runs when the primary has no key, is unreachable, rate limited,
          rejects the key or has no credit, or fails on its side. Rejected audio and silence do not
          use the backup. Changes apply to the next dictation.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          The deployment decides which endpoints and regions a key may reach. OpenRouter picks the
          upstream transcription provider itself; only its EU endpoint, which Mistral models
          require, keeps processing in the EU.
        </p>
      </div>
      {policy.isLoading || options.isLoading ? (
        <p role="status">Loading dictation settings...</p>
      ) : policy.error || options.error ? (
        <div role="status" className="space-y-2">
          <p>
            This Rudi release has no platform dictation settings. Update Rudi to edit them here.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              void policy.refetch();
              void options.refetch();
            }}
          >
            Reload
          </Button>
        </div>
      ) : (
        policy.data &&
        options.data && (
          <>
            <div role="status" className="space-y-2 rounded-3xl border p-3 text-sm">
              <p>
                {policy.data.source === 'none'
                  ? "No platform default is saved. Tenants without their own policy use the deployment's DeepInfra default when the deployment approves it, the built-in default on development backends, and otherwise have no dictation."
                  : `Tenants without their own policy use this default, revision ${policy.data.revision}.`}
              </p>
              {policy.data.source === 'platform' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    setRemoveError(null);
                    remove
                      .mutateAsync(policy.data!.revision)
                      .catch(() =>
                        setRemoveError('Could not remove the platform default. Reload and retry.')
                      );
                  }}
                >
                  Remove platform default
                </Button>
              )}
              {removeError && (
                <p role="alert" className="text-destructive">
                  {removeError}
                </p>
              )}
            </div>
            <VoiceWritingPolicyForm
              key={`platform/${policy.data.source}`}
              scope="platform"
              saved={policy.data}
              options={options.data}
              pending={save.isPending}
              onSetKey={onSetKey}
              onSave={async (draft, expectedRevision) => {
                await save.mutateAsync({ expected_revision: expectedRevision, policy: draft });
              }}
            />
          </>
        )
      )}
    </section>
  );
}
